<script setup lang="ts">
interface Props {
  dynamicId?: string;
  width?: string;
  title?: string;
  errorMessage?: string;
  isRequired?: boolean;
  showTitle?: boolean;
  modelValue?: string;
  yearly?: boolean;
  index?: number;
}

const props = withDefaults(defineProps<Props>(), {
  dynamicId: "",
  width: "100%",
  title: "",
  errorMessage: "",
  isRequired: false,
  showTitle: true,
  modelValue: "",
  yearly: true,
  index: 0,
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  returnValue: [
    payload: { type: string; index: number; value: string; required: boolean; title: string },
  ];
}>();

const showCalendar = ref(false);
const chosenDate = ref<Record<string, unknown> | false>(false);
const inputValue = ref(`Please enter your ${props.title.toLowerCase() || "date"}`);

const isTitleVisible = computed(() => props.title !== "" && props.showTitle);

function handleDateSelect(date: { full: string }) {
  inputValue.value = date.full;
  chosenDate.value = date;
  showCalendar.value = false;
  emit("update:modelValue", date.full);
  emit("returnValue", {
    type: "date",
    index: props.index,
    value: date.full,
    required: props.isRequired,
    title: props.title,
  });
}
</script>

<template>
  <div
    class="date-container"
    :style="{ width }"
  >
    <span
      v-if="isTitleVisible"
      class="date-container__title"
    >
      {{ title }}
      <i v-if="isRequired">*</i>
    </span>
    <div
      :id="dynamicId"
      :style="`width: ${width}`"
      class="date-container__input input"
      @click="showCalendar = !showCalendar"
    >
      {{ inputValue }}
    </div>
    <div
      v-if="showCalendar"
      class="date-container__overlay"
      @click="showCalendar = false"
    />
    <div
      v-if="showCalendar"
      class="date-container__dropdown"
    >
      <Calendar
        :width="'100%'"
        class="date-container__calendar"
        :chosen-date="chosenDate"
        :yearly="yearly"
        :fixed="true"
        @return-date="handleDateSelect"
      />
    </div>
    <BannerError :message="!chosenDate ? errorMessage : ''" />
  </div>
</template>

<style lang="scss">
.date-container {
  position: relative;

  &__input {
    cursor: pointer;
    margin-bottom: 5px;
  }

  &__title,
  &__error {
    display: flex;
    align-items: center;
  }

  &__title {
    color: var(--color-grey-1);

    i {
      color: var(--color-pink-1);
    }
  }

  &__overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 0.25rem);
    left: 0;
    z-index: 10000;
    background-color: var(--color-white-1);
    border-radius: 0.4rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 100%;
    width: max-content;
  }
}

.uppercase {
  text-transform: uppercase;
}
</style>
